import { ref, watch } from 'vue'

import { useRoute, useNuxtApp } from '#app'
import { useI18n } from '#imports'

export const usePwaUpdate = () => {
  if (import.meta.server) {
    return {
      needsRefresh: ref(false),
      registerPwaUpdate: () => {},
      clearAllCaches: () => Promise.resolve(false),
      forceReload: () => {},
    }
  }

  const route = useRoute()
  const { t } = useI18n()
  const { $toast } = useNuxtApp()

  const needsRefresh = ref(false)
  const updateSW = ref(null)

  // 檢查是否在遊戲頁面
  const isGamePage = () => {
    return route.path.startsWith('/game')
  }

  // 顯示更新 Toast
  const showUpdateToast = () => {
    // 使用 $toast 顯示 PWA 更新提示
    $toast.info(t('description.new_version'), {
      autoClose: false,
      closeButton: true,
      position: 'top-center',
      toastId: 'pwa-update',
      onClick: () => {
        updateSW.value?.()
      },
    })
  }

  // 清除所有 Service Worker 快取
  const clearAllCaches = async () => {
    if (typeof window === 'undefined' || !('caches' in window)) return false

    try {
      const cacheNames = await caches.keys()
      await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))
      console.log('[PWA] All caches cleared')
      return true
    } catch (error) {
      console.error('[PWA] Failed to clear caches:', error)
      return false
    }
  }

  // 強制重新載入（用於修復損壞的快取）
  const forceReload = async () => {
    await clearAllCaches()
    // 取消註冊 Service Worker
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      for (const registration of registrations) {
        await registration.unregister()
      }
    }
    // 強制重新載入頁面（跳過快取）
    window.location.reload()
  }

  // 監聽路由變化
  watch(
    () => route.path,
    (newPath, oldPath) => {
      // 從遊戲頁離開且有待顯示的更新提示
      if (!newPath.startsWith('/game') && needsRefresh.value) {
        showUpdateToast()
      }
    }
  )

  // 註冊 Service Worker 更新邏輯
  const registerPwaUpdate = async () => {
    if (typeof window === 'undefined') return

    try {
      const { registerSW } = await import('virtual:pwa-register')

      updateSW.value = registerSW({
        onNeedRefresh() {
          needsRefresh.value = true
          // 如果不在遊戲頁面，立即顯示更新提示
          if (!isGamePage()) {
            showUpdateToast()
          }
        },
        onOfflineReady() {
          console.log('[PWA] App ready to work offline')
        },
        onRegistered(registration) {
          console.log('[PWA] SW Registered:', registration)
        },
        onRegisterError(error) {
          console.error('[PWA] SW registration error:', error)
          // 如果 SW 註冊失敗，可能是快取損壞，嘗試清除
          clearAllCaches()
        },
      })
    } catch (error) {
      console.error('[PWA] Failed to register SW:', error)
      // 如果載入 SW 模組失敗，可能是快取問題
      clearAllCaches()
    }
  }

  return {
    needsRefresh,
    registerPwaUpdate,
    clearAllCaches,
    forceReload,
  }
}
